
// Audio.js

class AudioConnectivity {
    constructor(game_id, token, baseUrl, setUserInputCallback) {

        this.game_id = game_id;
        this.token = token;
        this.BASE_URL = baseUrl;
        this.setUserInput = setUserInputCallback;

        this.userVADChannel = null;
        this.userInputMicrophone = null;

        this.selectedVoice = null;
        this.gmVoiceChannel = null;
        this.gmOutputSpeaker = null;

        this.isUserInputConnected = false;
        this.isGMVoiceConnected = false;
        this.isGreeted = false;

        this.isRecording = false;
        this.audioChunksRef = [];
        this.mediaRecorder = null;

        this.realtime_url = "https://api.openai.com/v1/realtime";
    }


    createSilentAudioTrack() {
        const audioContext = new AudioContext();
        const oscillator = audioContext.createOscillator();
        oscillator.frequency.value = 0;
        const gain = audioContext.createGain();
        gain.gain.value = 0;
        const destination = audioContext.createMediaStreamDestination();
        oscillator.connect(gain).connect(destination);
        oscillator.start();
        return destination.stream.getAudioTracks()[0];
    }


    disconnectGMVoiceConnection() {
        if (this.gmVoiceChannel) {
            this.gmVoiceChannel.close();
            this.isGMVoiceConnected = false;
            console.log("GM Voice connection disconnected!");
        }
    }

    disconnectUserVADConnection() {
        if (this.userVADChannel) {
            this.userVADChannel.close();
            this.isUserInputConnected = false;
            console.log("User Input connection disconnected!");
        }
    }

    async initGMVoiceConnection(selectedVoice) {

        this.selectedVoice = selectedVoice;

        try {
            // Step 1: Fetch ephemeral key from your server
            const tokenResponse = await fetch(`${this.BASE_URL}/rtc-ai-voice-session`);
            const data = await tokenResponse.json();
            const EPHEMERAL_KEY = data.client_secret.value;
            //console.log("Ephemeral Key:", EPHEMERAL_KEY);

            // Step 2: Create RTCPeerConnection
            const pc = new RTCPeerConnection();

            // Step 3: Handle remote audio playback
            const audioEl = document.createElement("audio");
            this.gmOutputSpeaker = audioEl;
            this.gmOutputSpeaker.muted = this.isSpeakerMuted;


            audioEl.autoplay = true; // Autoplay remote audio
            pc.ontrack = (event) => {
                //console.log("Remote audio stream received:", event.streams[0]);
                audioEl.srcObject = event.streams[0];
                if (!document.body.contains(audioEl)) {
                    document.body.appendChild(audioEl);
                }
            };

            // Step 4: Add local audio track (for microphone input)
            // This prevents audio input to the output-only channel
            const silentTrack = this.createSilentAudioTrack();
            pc.addTrack(silentTrack);

            // Step 5: Set up data channel
            const dc = pc.createDataChannel("output-events");
            dc.addEventListener("open", () => {
                //console.log("Output data channel is open.");

                const sessionUpdate = {
                    "type": "session.update",
                    "session": {
                        "voice": this.selectedVoice
                    }
                }
                dc.send(JSON.stringify(sessionUpdate));

                if (!this.isGreeted) {
                    const responseCreate = {
                        type: "response.create",
                        response: {
                            modalities: ["audio", "text"],
                            instructions: `Say in a friendly and warm tone: "Welcome!  You are connected to Game Master Dot Chat!"`,
                        },
                    };
                    dc.send(JSON.stringify(responseCreate));
                    this.isGreeted = true;
                }
            });
            dc.addEventListener("message", (e) => {
                //const realtimeEvent = JSON.parse(e.data);
                //console.log("Realtime Event:", realtimeEvent);
            });
            dc.addEventListener("error", (e) => {
                console.error("Data channel error:", e);
            });


            this.gmVoiceChannel = dc;

            // Step 6: Create SDP offer and send to OpenAI
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            const sdpResponse = await fetch(`${this.realtime_url}`, {
                method: "POST",
                body: offer.sdp,
                headers: {
                    Authorization: `Bearer ${EPHEMERAL_KEY}`,
                    "Content-Type": "application/sdp",
                },
            });

            // Step 7: Set remote description
            const answer = {
                type: "answer",
                sdp: await sdpResponse.text(),
            };
            await pc.setRemoteDescription(answer);

            // Save the connection and mark it as connected
            this.isGMVoiceConnected = true;
            console.log("GM Voice connection established!");
            return true;
        } catch (error) {
            console.error("Error initializing GM Voice connection:", error);
            return false;
        }
    }

    async initUserVADConnection(micMuted) {

        try {
            // Step 1: Fetch ephemeral key from your server
            const tokenResponse = await fetch(`${this.BASE_URL}/rtc-user-input-transcription-session`);
            const data = await tokenResponse.json();
            const EPHEMERAL_KEY = data.client_secret.value;
            //console.log("Ephemeral Key:", EPHEMERAL_KEY);

            // Step 2: Create RTCPeerConnection
            const pc = new RTCPeerConnection();

            // Step 3: Add real microphone input
            const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const track = mediaStream.getTracks()[0];
            this.userInputMicrophone = track;
            this.userInputMicrophone.enabled = !micMuted;
            pc.addTrack(track);

            // Step 4: Set up data channel
            const dc = pc.createDataChannel("input-events");
            dc.addEventListener("open", () => {
                //console.log("Input data channel is open.");
            });

            // Step 4: Handle transcription events
            dc.addEventListener("message", (e) => {
                const realtimeEvent = JSON.parse(e.data);
                //console.log("Realtime Event:", realtimeEvent);
                if (realtimeEvent.type === "conversation.item.input_audio_transcription.completed") {
                    //console.log("User said:", realtimeEvent.transcript);
                    this.setUserInput("You said: " + realtimeEvent.transcript);
                }
            });
            dc.addEventListener("error", (e) => {
                console.error("Data channel error:", e);
            });

            this.userVADChannel = dc;

            // Step 5: Create SDP offer and send to OpenAI
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            const sdpResponse = await fetch(`${this.realtime_url}`, {
                method: "POST",
                body: offer.sdp,
                headers: {
                    Authorization: `Bearer ${EPHEMERAL_KEY}`,
                    "Content-Type": "application/sdp",
                },
            });

            // Step 6: Set remote description
            const answer = {
                type: "answer",
                sdp: await sdpResponse.text(),
            };
            await pc.setRemoteDescription(answer);

            // Mark the input connection as connected
            console.log("User Input connection established!");
            this.isUserInputConnected = true;
            return true;
        } catch (error) {
            console.error("Error initializing User Input connection:", error);
        }
    }



    readText(text_to_read) {

        if (!this.isGMVoiceConnected || !this.gmVoiceChannel || this.gmVoiceChannel.readyState !== "open") {
            console.warn("GM Voice connection is not established - attempting to reconnect...");
            this.initGMVoiceConnection(this.selectedVoice);
        }
        const conversationItemDelete = {
            event_id: `event_1`,
            type: "conversation.item.delete",
            item_id: `msg_1`,
        };
        this.gmVoiceChannel.send(JSON.stringify(conversationItemDelete));

        const conversationItem = {
            event_id: `event_1`,
            type: "conversation.item.create",
            previous_item_id: null,
            item: {
                id: `msg_1`,
                type: "message",
                role: "user",
                content: [
                    {
                        type: "input_text",
                        text: `Recite the following narrative - exactly as it is - with appropriate tone, pacing, and passion. Narrative: \n"""\n${text_to_read}\n"""`,
                    },
                ],
            },
        };
        this.gmVoiceChannel.send(JSON.stringify(conversationItem));
        //console.log("Sent to server:", conversationItem);

        const responseCreate = {
            type: "response.create",
            response: {
                modalities: ["audio", "text"],
                instructions: `You are the game master for a role-playing game.  Your task is to recite the provided narrative.  Do not say anything else.`,
            },
        };
        this.gmVoiceChannel.send(JSON.stringify(responseCreate));
    }

    toggleMicMute() {
        if (this.userInputMicrophone) {
            this.userInputMicrophone.enabled = !this.userInputMicrophone.enabled;
        }
    }

    toggleSpeakerMute() {
        if (this.gmOutputSpeaker) {
            this.gmOutputSpeaker.muted = !this.gmOutputSpeaker.muted;
        }
    }




    async handleRecordClick() {

        if (this.isRecording) {
            // Stop recording
            this.mediaRecorder?.stop();
        } else {
            // Start recording
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.mediaRecorder = new MediaRecorder(stream);

            this.mediaRecorder.ondataavailable = (event) => {
                this.audioChunksRef.push(event.data);
            };

            this.mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(this.audioChunksRef, { type: 'audio/webm' });
                const audioFile = new File([audioBlob], `${Date.now()}.webm`, { type: 'audio/webm' });

                // Handle the recorded audio
                const formData = new FormData();
                formData.append('audio_file', audioFile);

                try {
                    const response = await fetch(`${this.BASE_URL}/v1/speech-to-text/${this.game_id}`, {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'Authorization': `Bearer ${this.token}`,
                        },
                        mode: 'cors', // Ensuring the request is made with CORS mode
                    });
                    const data = await response.json();

                    if (data.transcription) {
                        this.setUserInput("You said: " + data.transcription);
                    }
                } catch (error) {
                    console.error('Error transcribing audio:', error);
                }

                // Clear recorded chunks
                this.audioChunksRef = [];
                this.isRecording = false;
            };

            this.mediaRecorder.start();
            this.isRecording = true;
        }
    }


}

export { AudioConnectivity };
